import { useEffect, useMemo, useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { adminApi, download, parseError } from "./api";
import { ConfirmButton, StatusBadge } from "./ui";
import { fmtDate, fmtTime } from "@/lib/format";

const KINDS = [["stories", "News / Stories"], ["events", "Events"]];
const ACTION_LABEL = { create: "Create as Draft", update: "Update existing record", skip: "Skip this row", reject: "Rejected" };
const MAX_MB = 5;

const Templates = ({ kind }) => (
  <div className="flex flex-wrap items-center gap-2 text-sm">
    <span className="font-bold">Templates:</span>
    {["xlsx", "csv"].map((f) => (
      <button key={f} type="button" className="admin-btn-outline inline-flex items-center gap-2" data-testid={`import-template-${kind}-${f}`}
        onClick={() => download(`/templates/${kind}`, { format: f }).catch((e) => toast.error(parseError(e).message))}>
        <Download className="h-4 w-4" aria-hidden="true" />{f.toUpperCase()}
      </button>
    ))}
    <span className="text-xs text-ink-muted">The XLSX template includes an Instructions sheet. The example row (id = EXAMPLE) is never imported.</span>
  </div>
);

const Help = () => (
  <details className="border border-line bg-white p-4 text-sm" data-testid="import-help">
    <summary className="cursor-pointer font-bold text-brand">Spreadsheet rules</summary>
    <ul className="mt-3 list-disc space-y-1 pl-5 text-ink-muted">
      <li>Required to create a draft: <b>title</b> and <b>shortSummary</b>. Publishing has extra requirements (stories: category, publicationDate; events: startDateTime, mode, venue or joining details).</li>
      <li>Dates are Bengaluru time (Asia/Kolkata): <code>YYYY-MM-DD HH:MM</code> or <code>DD/MM/YYYY HH:MM</code>. Ambiguous dates are flagged, never guessed.</li>
      <li>Multiple values (domains, institutions, who might benefit, speakers) are separated by semicolons. Speakers: <code>Name | Affiliation; Name</code>.</li>
      <li>New records are always created as <b>Draft</b>. A status column is only a suggestion you can apply row by row.</li>
      <li>Empty cells never overwrite existing values, and slugs of existing records never change.</li>
      <li>Image columns take image URLs only. Formulas are not imported. Max {MAX_MB} MB and 2,000 rows per file.</li>
    </ul>
  </details>
);

const SelectStep = ({ onInspected }) => {
  const [kind, setKind] = useState("stories");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) return setError("Choose a .csv or .xlsx file.");
    if (!/\.(csv|xlsx)$/i.test(file.name)) return setError("Only .csv and .xlsx files are supported.");
    if (file.size > MAX_MB * 1024 * 1024) return setError(`Files must be ${MAX_MB} MB or smaller.`);
    const body = new FormData();
    body.append("kind", kind);
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await adminApi.post("/import/inspect", body);
      onInspected(data);
    } catch (err) {
      setError(parseError(err).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-6 border border-line bg-white p-6" data-testid="import-step-select">
      <fieldset>
        <legend className="text-sm font-bold">1. What are you importing?</legend>
        <div className="mt-3 flex flex-wrap gap-4">
          {KINDS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 font-semibold">
              <input type="radio" name="kind" value={k} checked={kind === k} onChange={() => setKind(k)} className="accent-[#082F6B]" data-testid={`import-kind-${k}`} />{label}
            </label>
          ))}
        </div>
      </fieldset>
      <Templates kind={kind} />
      <div className="space-y-2">
        <label htmlFor="import-file" className="text-sm font-bold">2. Choose a file (.csv or .xlsx)</label>
        <input id="import-file" type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFile(e.target.files[0] || null)} className="block text-sm" data-testid="import-file-input" />
      </div>
      {error && <p role="alert" className="font-semibold text-[#B42318]" data-testid="import-select-error">{error}</p>}
      <button type="submit" disabled={busy} className="admin-btn" data-testid="import-upload-button">{busy ? "Reading file…" : "Upload & detect columns"}</button>
    </form>
  );
};

const MapStep = ({ info, onBack, onPreview }) => {
  const [sheetName, setSheetName] = useState(info.sheets[0].name);
  const sheet = info.sheets.find((s) => s.name === sheetName);
  const [mapping, setMapping] = useState(sheet.suggested);
  const [slash, setSlash] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setMapping(sheet.suggested), [sheet]);

  const counts = Object.values(mapping).filter(Boolean).reduce((m, f) => ({ ...m, [f]: (m[f] || 0) + 1 }), {});
  const doubled = Object.keys(counts).filter((f) => counts[f] > 1);
  const missing = info.fields.filter((f) => f.required && !counts[f.name]).map((f) => f.name);
  const updateOnly = counts.id || counts.slug;
  const blocked = sheet.issues.length > 0 || doubled.length > 0 || (missing.length > 0 && !updateOnly) || !sheet.rowCount;

  const preview = async () => {
    setBusy(true);
    setError("");
    const req = { sheet: sheetName, mapping, slashFormat: slash };
    try {
      const { data } = await adminApi.post(`/import/${info.sessionId}/preview`, req);
      onPreview(req, data);
    } catch (e) {
      setError(parseError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 border border-line bg-white p-6" data-testid="import-step-map">
      <p className="text-sm"><b>{info.filename}</b> · {KINDS.find(([k]) => k === info.kind)[1]}</p>
      {info.sheets.length > 1 && (
        <div className="space-y-2">
          <label htmlFor="import-sheet" className="text-sm font-bold">Worksheet</label>
          <select id="import-sheet" value={sheetName} onChange={(e) => setSheetName(e.target.value)} className="field max-w-sm" data-testid="import-sheet-select">
            {info.sheets.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.rowCount} rows)</option>)}
          </select>
        </div>
      )}
      {sheet.issues.map((i) => <p key={i} role="alert" className="font-semibold text-[#B42318]" data-testid="import-sheet-issue">{i}</p>)}
      {!sheet.issues.length && !sheet.rowCount && <p role="alert" className="font-semibold text-[#B42318]">This worksheet has headings but no data rows.</p>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-line text-xs uppercase tracking-[0.1em] text-ink-muted"><tr><th className="p-2">Spreadsheet column</th><th className="p-2">Sample values</th><th className="p-2">Maps to field</th></tr></thead>
          <tbody className="divide-y divide-line">
            {sheet.headers.map((h, i) => (
              <tr key={h || i}>
                <td className="p-2 font-bold">{h || <em className="text-[#B42318]">No heading</em>}</td>
                <td className="max-w-[18rem] truncate p-2 text-ink-muted">{sheet.sample.map((r) => r[i]).filter(Boolean).slice(0, 2).join(" · ") || "—"}</td>
                <td className="p-2">
                  <label htmlFor={`map-${i}`} className="sr-only">Field for column {h}</label>
                  <select id={`map-${i}`} value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || null })}
                    className={`field ${counts[mapping[h]] > 1 ? "border-[#B42318]" : ""}`} data-testid={`import-map-${i}`}>
                    <option value="">Ignore this column</option>
                    {info.fields.map((f) => <option key={f.name} value={f.name}>{f.name}{f.required ? " (required)" : ""}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {doubled.length > 0 && <p role="alert" className="font-semibold text-[#B42318]" data-testid="import-map-ambiguous">Mapped more than once: {doubled.join(", ")}. Map each field from one column only.</p>}
      {missing.length > 0 && (
        <p role="alert" className={`font-semibold ${updateOnly ? "text-[#8A5A00]" : "text-[#B42318]"}`} data-testid="import-map-missing">
          Required fields not mapped: {missing.join(", ")}.{updateOnly ? " Rows can still update existing records matched by id or slug, but new rows will be rejected." : ""}
        </p>
      )}
      <div className="space-y-2">
        <label htmlFor="import-slash" className="text-sm font-bold">Dates written with slashes (e.g. 03/04/2026) are</label>
        <select id="import-slash" value={slash} onChange={(e) => setSlash(e.target.value)} className="field max-w-sm" data-testid="import-slash-format">
          <option value="">Not specified — flag ambiguous dates</option>
          <option value="DMY">DD/MM/YYYY (day first)</option>
          <option value="MDY">MM/DD/YYYY (month first)</option>
        </select>
      </div>
      {error && <p role="alert" className="font-semibold text-[#B42318]" data-testid="import-map-error">{error}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onBack} className="admin-btn-outline" data-testid="import-back-select">Choose another file</button>
        <button type="button" onClick={preview} disabled={busy || blocked} className="admin-btn" data-testid="import-preview-button">{busy ? "Validating…" : "Preview & validate"}</button>
      </div>
    </div>
  );
};

const fmtVal = (v) => (Array.isArray(v) ? v.map((x) => (typeof x === "object" && x ? [x.name, x.affiliation].filter(Boolean).join(" | ") : x)).join("; ") : v === null || v === undefined || v === "" ? "(empty)" : String(v));

const RowLine = ({ r, decision, onChange }) => {
  const action = decision.action;
  const base = r.match ? r.match.status : "Draft";
  const canStatus = r.suggestedStatus && r.suggestedStatus !== base && ["create", "update"].includes(action);
  return (
    <tr className="align-top" data-testid={`import-row-${r.row}`}>
      <td className="p-2 font-bold">{r.row}</td>
      <td className="p-2">
        <p className="font-bold">{r.title || <em className="text-ink-muted">No title</em>}</p>
        <details className="mt-1 text-xs">
          <summary className="cursor-pointer text-brand">Values{r.changes.length ? " & changes" : ""}</summary>
          <dl className="mt-2 space-y-1">{Object.entries(r.values).map(([k, v]) => <div key={k}><dt className="inline font-bold">{k}: </dt><dd className="inline break-words">{fmtVal(v)}</dd></div>)}</dl>
          {r.changes.length > 0 && (
            <ul className="mt-2 space-y-1 border-t border-line pt-2" data-testid={`import-changes-${r.row}`}>
              {r.changes.map((c) => <li key={c.field}><b>{c.field}</b>: <span className="line-through opacity-70">{fmtVal(c.old)}</span> → {fmtVal(c.new)}</li>)}
            </ul>
          )}
        </details>
      </td>
      <td className="p-2 text-xs">
        {r.match && (
          <p data-testid={`import-match-${r.row}`}>
            <b className={r.match.uncertain ? "text-[#8A5A00]" : ""}>{r.match.uncertain ? "Possible duplicate" : "Existing record"}</b> by {r.match.by}: {r.match.title} <StatusBadge status={r.match.status} />
          </p>
        )}
        {r.duplicateOfRow && <p className="font-bold" data-testid={`import-dup-${r.row}`}>Duplicate of row {r.duplicateOfRow}</p>}
        {!r.match && !r.duplicateOfRow && !r.example && <p className="text-ink-muted">New</p>}
      </td>
      <td className="max-w-[22rem] p-2 text-xs">
        {[...r.errors, ...(action === "update" ? r.updateErrors : []), ...(action !== "update" ? r.createErrors : [])].map((m) => <p key={m} className="font-semibold text-[#B42318]">{m}</p>)}
        {r.warnings.map((m) => <p key={m} className="text-[#8A5A00]">{m}</p>)}
        {r.notes.map((m) => <p key={m} className="text-ink-muted">{m}</p>)}
      </td>
      <td className="p-2">
        {r.actions.length > 1 ? (
          <>
            <label htmlFor={`act-${r.row}`} className="sr-only">Action for row {r.row}</label>
            <select id={`act-${r.row}`} value={action} onChange={(e) => onChange({ ...decision, action: e.target.value })} className="field text-sm" data-testid={`import-action-${r.row}`}>
              {r.actions.map((a) => <option key={a} value={a}>{a === "create" && (r.match || r.duplicateOfRow) ? "Create as separate Draft (not a duplicate)" : ACTION_LABEL[a]}</option>)}
            </select>
          </>
        ) : (
          <span className={`text-sm font-bold ${action === "reject" ? "text-[#B42318]" : ""}`} data-testid={`import-action-${r.row}`}>{ACTION_LABEL[action]}</span>
        )}
        {action === "create" && <p className="mt-1 text-xs text-ink-muted">Will be created as {decision.applyStatus ? r.suggestedStatus : "Draft"}</p>}
        {action === "update" && <p className="mt-1 text-xs text-ink-muted">Status stays {decision.applyStatus ? `→ ${r.suggestedStatus}` : base}</p>}
        {canStatus && (
          <label className="mt-2 flex items-start gap-2 text-xs">
            <input type="checkbox" checked={Boolean(decision.applyStatus)} disabled={r.statusErrors.length > 0} onChange={(e) => onChange({ ...decision, applyStatus: e.target.checked })} className="mt-0.5 accent-[#082F6B]" data-testid={`import-apply-status-${r.row}`} />
            <span>Set status to <b>{r.suggestedStatus}</b> (from spreadsheet){r.statusErrors.length > 0 && <span className="block text-[#B42318]">Not possible: {r.statusErrors.join(" ")}</span>}</span>
          </label>
        )}
      </td>
    </tr>
  );
};

const PreviewStep = ({ info, req, data, onBack, onDone }) => {
  const [decisions, setDecisions] = useState(() => Object.fromEntries(data.rows.map((r) => [r.row, { action: r.defaultAction, applyStatus: false }])));
  const [confirmPublished, setConfirmPublished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const chosen = useMemo(() => {
    const c = { create: 0, update: 0, skip: 0, reject: 0, publishedUpdates: 0, statusChanges: 0 };
    data.rows.forEach((r) => {
      const d = decisions[r.row];
      c[d.action] += 1;
      if (d.action === "update" && r.publishedUpdate) c.publishedUpdates += 1;
      if (d.applyStatus && ["create", "update"].includes(d.action)) c.statusChanges += 1;
    });
    return c;
  }, [decisions, data.rows]);
  const k = data.counts;

  const commit = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data: res } = await adminApi.post(`/import/${info.sessionId}/commit`, { ...req, decisions, confirmPublishedUpdates: confirmPublished });
      onDone(res);
    } catch (e) {
      setError(parseError(e).message);
      toast.error(parseError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-step-preview">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="import-counts">
        {[["Rows", k.rows], ["Valid new", k.new], ["Duplicates", k.duplicates], ["Updates possible", k.updatesAvailable], ["Skipped by default", k.skipped], ["Rejected", k.rejected]].map(([l, v]) => (
          <div key={l} className="border border-line bg-white p-4"><p className="text-2xl font-extrabold text-brand">{v}</p><p className="text-xs font-bold">{l}</p></div>
        ))}
      </div>
      <p className="text-sm text-ink-muted">Nothing has been saved yet. Review each row and choose what should happen. New records are created as Draft unless you tick “Set status”.</p>
      <div className="overflow-x-auto border border-line bg-white">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="border-b border-line bg-mist text-xs uppercase tracking-[0.1em] text-ink-muted"><tr><th className="p-2">Row</th><th className="p-2">Record</th><th className="p-2">Duplicate check</th><th className="p-2">Errors & warnings</th><th className="p-2">Action</th></tr></thead>
          <tbody className="divide-y divide-line">
            {data.rows.map((r) => <RowLine key={r.row} r={r} decision={decisions[r.row]} onChange={(d) => setDecisions((cur) => ({ ...cur, [r.row]: d }))} />)}
          </tbody>
        </table>
      </div>
      {chosen.publishedUpdates > 0 && (
        <label className="flex items-start gap-3 border border-[#8A5A00] bg-[#FFF7E6] p-4 text-sm font-semibold">
          <input type="checkbox" checked={confirmPublished} onChange={(e) => setConfirmPublished(e.target.checked)} className="mt-1 accent-[#082F6B]" data-testid="import-confirm-published" />
          I have reviewed the field changes to {chosen.publishedUpdates} published {chosen.publishedUpdates === 1 ? "record" : "records"}, which will change on the public site.
        </label>
      )}
      {error && <p role="alert" className="font-semibold text-[#B42318]" data-testid="import-commit-error">{error}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onBack} className="admin-btn-outline" data-testid="import-back-map">Back to column mapping</button>
        <ConfirmButton label={busy ? "Importing…" : "Import"} className="admin-btn" testId="import-commit-button"
          disabled={busy || (chosen.publishedUpdates > 0 && !confirmPublished) || chosen.create + chosen.update === 0}
          title="Confirm import"
          description={`${chosen.create} to create, ${chosen.update} to update (${chosen.publishedUpdates} published), ${chosen.skip} to skip, ${chosen.reject} rejected. ${chosen.statusChanges} status change(s) you selected. Rejected and skipped rows are kept in the import report.`}
          onConfirm={commit} />
      </div>
    </div>
  );
};

const History = ({ tick }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    adminApi.get("/import/history").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [tick]);
  if (!items) return null;
  return (
    <section className="mt-12" aria-labelledby="import-history-heading" data-testid="import-history">
      <h2 id="import-history-heading" className="text-xl font-extrabold text-brand">Import history</h2>
      {!items.length ? <p className="mt-3 text-sm text-ink-muted">No imports yet.</p> : (
        <div className="mt-4 overflow-x-auto border border-line bg-white">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-line bg-mist text-xs uppercase tracking-[0.1em] text-ink-muted"><tr><th className="p-2">When</th><th className="p-2">File</th><th className="p-2">Type</th><th className="p-2">Result</th><th className="p-2">Report</th></tr></thead>
            <tbody className="divide-y divide-line">
              {items.map((h) => (
                <tr key={h.id}>
                  <td className="p-2">{fmtDate(h.startedAt)}, {fmtTime(h.startedAt)}</td>
                  <td className="p-2">{h.filename}</td>
                  <td className="p-2">{h.kind}</td>
                  <td className="p-2">{h.status === "failed" && <b className="text-[#B42318]">Failed · </b>}{h.counts.created} created, {h.counts.updated} updated, {h.counts.skipped} skipped, {h.counts.rejected} rejected{h.counts.failed ? `, ${h.counts.failed} not processed` : ""}</td>
                  <td className="p-2"><button type="button" className="font-bold text-brand underline" onClick={() => download(`/import/history/${h.id}/report`, { format: "csv" })} data-testid={`import-history-report-${h.id}`}>CSV</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default function ImportWizard() {
  const [info, setInfo] = useState(null);
  const [req, setReq] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  const [tick, setTick] = useState(0);
  const reset = () => { setInfo(null); setReq(null); setPreviewData(null); setResult(null); };
  const step = result ? 4 : previewData ? 3 : info ? 2 : 1;

  return (
    <div data-testid="admin-import">
      <h1 className="flex items-center gap-3 text-3xl font-extrabold tracking-tight text-brand"><FileSpreadsheet className="h-7 w-7" aria-hidden="true" /> Import spreadsheet</h1>
      <ol className="mt-4 flex flex-wrap gap-2 text-xs font-bold" aria-label="Import steps">
        {["Select file", "Map columns", "Preview & validate", "Report"].map((s, i) => (
          <li key={s} aria-current={step === i + 1 ? "step" : undefined} className={`rounded-full px-3 py-1 ${step === i + 1 ? "bg-brand text-white" : "bg-white text-ink-muted"}`}>{i + 1}. {s}</li>
        ))}
      </ol>
      <div className="mt-6 space-y-6">
        <Help />
        {step === 1 && <SelectStep onInspected={setInfo} />}
        {step === 2 && <MapStep info={info} onBack={reset} onPreview={(r, d) => { setReq(r); setPreviewData(d); }} />}
        {step === 3 && <PreviewStep info={info} req={req} data={previewData} onBack={() => setPreviewData(null)} onDone={(r) => { setResult(r); setTick((n) => n + 1); }} />}
        {step === 4 && (
          <div className="space-y-4 border border-line bg-white p-6" data-testid="import-result">
            <h2 className="text-xl font-extrabold text-brand">{result.status === "failed" ? "Import stopped with an error" : "Import complete"}</h2>
            <p data-testid="import-result-counts">{result.counts.created} created · {result.counts.updated} updated · {result.counts.skipped} skipped · {result.counts.rejected} rejected{result.counts.failed ? ` · ${result.counts.failed} not processed` : ""}</p>
            {result.status === "failed" && <p role="alert" className="font-semibold text-[#B42318]">Rows already listed as created or updated were saved; the remaining rows were not processed. Each record is written in a single step, so no record is half-updated.</p>}
            <div className="flex flex-wrap gap-3">
              <button type="button" className="admin-btn-outline" onClick={() => download(`/import/history/${result.historyId}/report`, { format: "csv" })} data-testid="import-report-csv">Download row report (CSV)</button>
              <button type="button" className="admin-btn-outline" onClick={() => download(`/import/history/${result.historyId}/report`, { format: "xlsx" })} data-testid="import-report-xlsx">Download row report (XLSX)</button>
              <button type="button" className="admin-btn" onClick={reset} data-testid="import-start-over">Start another import</button>
            </div>
          </div>
        )}
      </div>
      <History tick={tick} />
    </div>
  );
}
